import React from "react";
import { Navigate, useParams } from "react-router-dom";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { getCloudSubscriptionById, getCloudSubscriptionBySerial } from "client/api/CloudSubscriptionApi";
import type { CloudSubscriptionDto } from "client/api/CloudSubscriptionApi";
import { NotFoundPage } from "pages/NotFoundPage";
import { cloudSubscriptionIdRouteParam } from "areas/cloud-subscriptions/cloud-subscription-routes";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

function resolveSubscriptionId(cloudSubscription: CloudSubscriptionDto): string | null {
  return cloudSubscription.licensingChannel.subscriptionId;
}

const unmappedRecordMessage =
  "This record cannot be opened in the unified view — it is not linked to a subscription. Log an issue.";

function RedirectToUnified({ cloudSubscription }: { cloudSubscription: CloudSubscriptionDto }) {
  const subscriptionId = resolveSubscriptionId(cloudSubscription);
  if (!subscriptionId) {
    return <NotFoundPage message={unmappedRecordMessage} />;
  }
  return <Navigate to={subscriptionRoutes.detail(subscriptionId).root} replace />;
}

export function CloudSubscriptionRedirect() {
  const params = useParams();
  const cloudSubscriptionId = params[cloudSubscriptionIdRouteParam];

  const { data, loading, error } = useAsyncRequest(getCloudSubscriptionById, cloudSubscriptionId || "");

  if (!cloudSubscriptionId) {
    return <NotFoundPage />;
  }

  return (
    <LoadablePage loading={loading} error={error}>
      {data ? <RedirectToUnified cloudSubscription={data} /> : <NotFoundPage />}
    </LoadablePage>
  );
}

export function CloudSubscriptionBySerialRedirect() {
  const { serialId } = useParams();
  const { data, loading, error } = useAsyncRequest(getCloudSubscriptionBySerial, serialId || "");

  if (!serialId) {
    return <NotFoundPage />;
  }

  return (
    <LoadablePage loading={loading} error={error}>
      {data ? <RedirectToUnified cloudSubscription={data} /> : <NotFoundPage />}
    </LoadablePage>
  );
}
